import InfoIcon from "@/assets/colorable/info.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import ProInfluencerIcon from "@/assets/images/pro_influencer.svg?react";
import AnimatedAccordion from "@/components/Common/animated/AnimatedAccordion";
import Button from "@/components/Common/Button";
import type {TManageCredits} from "@/hooks/useManageCredits";
import clsxm from "@/lib/clsxm";
import CreditInputField from "@/pages/plans/creditSystem/CreditInputField";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const ManageCreditsContent: FC<{
  credits: number;
  influencerFees: {pro?: number | null; regular?: number | null};
  manageCredits: TManageCredits;
}> = ({credits, influencerFees, manageCredits}) => {
  const {t, i18n} = useTranslation("common");
  const {
    inputValue,
    isAddMode,
    isAdvancedOptionsOpen,
    newBalance,
    selectedFee,
    setAdvancedOptionsOpen,
    setInputValue,
    setSelectedFee,
  } = manageCredits;

  const formatBalance = (value: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);

  const renderInfobox = (text: string) => (
    <div className="bg-primary-light-solid flex items-center gap-2 rounded-md p-3">
      <InfoIcon className="text-primary-sky size-3.5 shrink-0" />
      <span className="text-neutral-black text-xs font-normal">{text}</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <span className="text-neutral-black text-xs leading-3">{t("current_balance")}:</span>
        <span className="text-neutral-black text-xl leading-5 font-semibold">{formatBalance(credits)}</span>
      </div>
      {isAddMode && renderInfobox(t("manage_credits_add_info"))}
      <CreditInputField type={isAddMode ? "add" : "remove"} value={inputValue} onChange={setInputValue} />
      {isAddMode && (
        <div className="flex flex-col gap-5">
          <button
            onClick={() => setAdvancedOptionsOpen(!isAdvancedOptionsOpen)}
            className="flex items-center justify-center gap-2">
            <span className="text-primary-dark-solid py-3 text-center text-sm leading-3.5 font-medium">
              {t("advanced_options")}
            </span>
            <RightChevronIcon
              className={clsxm(
                "text-primary-dark-solid size-2.5 transition-all",
                isAdvancedOptionsOpen ? "-rotate-90" : "rotate-90",
              )}
            />
          </button>
          <AnimatedAccordion condition={isAdvancedOptionsOpen} className="flex flex-col gap-5 overflow-hidden">
            {renderInfobox(t("advanced_options_info"))}
            <div className="flex gap-5">
              <Button
                buttonStyle="White"
                shouldScaleOnHover={selectedFee !== "regular"}
                className={clsxm(
                  "min-h-15 w-full max-w-1/2 gap-2.5 py-5 enabled:border-2 sm:px-8",
                  selectedFee === "regular" && "border-primary-dark-solid bg-primary-powder",
                )}
                onClick={() => setSelectedFee(selectedFee === "regular" ? undefined : "regular")}>
                <span
                  className={clsxm(
                    "text-neutral-black text-sm text-nowrap sm:text-base",
                    selectedFee === "regular" && "text-primary-dark-solid",
                  )}>{`${t("fee")} (${influencerFees.regular}%)`}</span>
              </Button>
              {influencerFees.regular !== influencerFees.pro && (
                <Button
                  buttonStyle="White"
                  shouldScaleOnHover={selectedFee !== "pro"}
                  className={clsxm(
                    "min-h-15 w-full gap-2.5 py-5 enabled:border-2 sm:px-8",
                    selectedFee === "pro" && "border-primary-dark-solid bg-primary-powder",
                  )}
                  onClick={() => setSelectedFee(selectedFee === "pro" ? undefined : "pro")}>
                  <ProInfluencerIcon className="size-4 shrink-0 rounded-full" />
                  <span
                    className={clsxm(
                      "text-neutral-black text-sm text-nowrap sm:text-base",
                      selectedFee === "pro" && "text-primary-dark-solid",
                    )}>{`${t("fee")} (${influencerFees.pro}%)`}</span>
                </Button>
              )}
            </div>
          </AnimatedAccordion>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <span className="text-neutral-black text-xs leading-3">{t("new_balance")}:</span>
        <span className="text-neutral-black text-xl leading-5 font-semibold">{formatBalance(newBalance)}</span>
      </div>
      {!isAddMode && renderInfobox(t("manage_credits_remove_info"))}
    </div>
  );
};

export default ManageCreditsContent;
